import React from 'react';
import { Route, Navigate, Routes } from 'react-router';

import LoginPage from './containers/LoginPage';
import Dashboard from './containers/dashboard/Dashboard';
import NotFound from './containers/NotFound';
import Unauthorized from './containers/Unauthorized';
import ProtectedRoute from './components/security/ProtectedRoute';
import config from './constants/config';
import permissions from './constants/permissions';
import orderTabs from './constants/orderTabs';
import ConfigurationManagementPage from './containers/configuration-management/ConfigurationManagementPage';
import PermissionsManagementPage from './containers/permissions-management/PermissionsManagementPage';
import UpdateConfigurationManagementPage from './containers/configuration-management/UpdateConfigurationManagementPage';
import UsersPage from './containers/users/UsersPage';
import CreateUser from './containers/users/CreateUserPage';
import UpdateUser from './containers/users/UpdateUserPage';
import GameCatalogPage from './containers/game-catalog/GameCatalogPage';
import CheckoutPage from './containers/checkout/CheckoutPage';
import OrderDetailPage from './containers/orderDetailPage/OrderDetailPage';
import OrderHistoryPage from './containers/orderHistoryPage/OrderHistoryPage';
import InvoicesPage from './containers/invoicesPage/InvoicesPage';
import ReportPage from './containers/reports/ReportPage';
import ReportsPage from './containers/reports/ReportsPage';
import InvoiceDetailPage from './containers/invoiceDetailPage/InvoiceDetailPage';
import AdminFaqPage from './containers/faq/AdminFaqPage';
import FaqPage from './containers/faq/FaqPage';
import ContactUsConfigurationPage from './containers/contactUsConfigurationPage/ContactUsConfigurationPage';
import ContactUsPage from './containers/contactUsPrivate/ContactUsPage';
import AdminPrivacyPolicyPage from './containers/privacy-policy/AdminPrivacyPolicyPage';
import PrivacyPolicyPage from './containers/privacy-policy/PrivacyPolicyPage';
import AdminNewRetailerPage from './containers/new-retailer/AdminNewRetailerPage';
import DocumentListPage from './containers/PortalDocuments/DocumentListPage';
import CreateRolePage from './containers/permissions-management/CreateRolePage';
import MessageListPage from './containers/portalMessages/MessageListPage';
import GettingStartedPage from './containers/gettingStartedPage/GettingStartedPage';
import DashboardSettings from './containers/dashboard/settings/DashboardSettings';
import GamesListPage from './containers/gamesListPage/GamesListPage';
import RetailerGroupListPage from './containers/retailer-groups/retailer-group-list-page';
import EditRetailerGroupPage from './containers/retailer-groups/edit-retailer-group';
import PlanogramListPage from './containers/planograms/planogram-list-page';
import EditPlanogramPage from './containers/planograms/edit-planogram';
import RetailerPlanogramListPage from './containers/retailer-planogram-view/retailer-planogram-list-page';
import CognitoLoadingScreen from './containers/cognito/cognito-loading-screen';
import ReturnsListPage from './containers/returns/ReturnsListPage';
import EditCourierPage from './containers/courier/edit-courier-page';
import CourierListPage from './containers/courier/courier-list-page';
import CreateReturn from './containers/returns/CreateReturn';
import ViewReturn from './containers/returns/ViewReturn';
import { hasPermission } from './constants/utils';
import GameHighlightsPage from './containers/gameHighlights/GameHighlightsPage';

const {
    LOGIN_PAGE,
    DASHBOARD,
    UNAUTHORIZED,
    NOT_FOUND,
    CONFIGURATION_MANAGEMENT,
    UPDATE_CONFIGURATION_MANAGEMENT,
    PERMISSIONS_CONFIGURATION_MANAGEMENT,
    CREATE_USER,
    UPDATE_USER,
    USER_MANAGEMENT,
    GAMES,
    RETAILER_PLANOGRAM_LIST,
    CHECKOUT,
    ORDER,
    INVOICE,
    REPORTS,
    FAQ_PAGE,
    CONTACT_US_PAGE,
    CONTACT_US_CONFIGURATION,
    FAQ_MANAGEMENT_PRIVATE,
    FAQ_MANAGEMENT_PUBLIC,
    PRIVACY_POLICY_PAGE,
    PRIVACY_POLICY_MANAGEMENT,
    NEW_RETAILER_POLICY,
    UPLOAD_MANAGEMENT,
    CREATE_ROLE,
    MESSAGE_MANAGEMENT,
    // VIRTUAL_TOUR_CONFIGURATION,
    GETTING_STARTED,
    DASHBOARD_SETTINGS,
    GAMES_LIST,
    RETAILER_GROUP_LIST,
    RETAILER_GROUP_UPDATE,
    RETAILER_GROUP_CREATE,
    PLANOGRAM_UPDATE,
    PLANOGRAM_CREATE,
    PLANOGRAM_LIST,
    RETAILER_RETURN_LIST,
    RETAILER_RETURN_CREATE,
    COURIER_UPDATE,
    COURIER_CREATE,
    COURIER_LIST,
    GAME_HIGHLIGHTS,
    GAMES_REVIEW,
} = config.ROUTE_URLS;
const { COGNITO_ENABLED } = config;
const {
    LIST_USERS,
    CREATE_USERS,
    VIEW_USERS,
    UPDATE_SYS_CONFIGS,
    LOGO_CONFIG_UPLOAD,
    VIEW_SYS_CONFIGS,
    DASHBOARD_PAGE,
    CREATE_FAQ,
    UPDATE_CONTACT_US,
    CREATE_PRIVACY_POLICY,
    CREATE_ROLES,
    GAME_PAGE,
    IN_PROGRESS_ORDERS,
    PAST_ORDERS,
    ORDER_DETAILS,
    INVOICE_PAGE,
    INVOICE_DETAILS,
    LIST_REPORT,
    FAQ,
    CONTACT_US,
    PRIVACY_POLICY,
    CREATE_NEW_RETAILER_POLICY,
    DISPLAY_GAMES_LIST,
    DISPLAY_RETAILER_GROUP_LIST,
    CREATE_RETAILER_GROUP_LIST,
    VIEW_RETAILER_GROUP_LIST,
    UPDATE_RETAILER_GROUP_LIST,
    ROLE_PLANOGRAM_CREATE,
    ROLE_PLANOGRAM_UPDATE,
    ROLE_PLANOGRAM_LIST,
    ROLE_PLANOGRAM_VIEW,
    ROLE_RETURN_LIST,
    ROLE_RETURN_CREATE,
    ROLE_RETURN_VIEW,
    ROLE_COURIER_UPDATE,
    ROLE_COURIER_CREATE,
    ROLE_COURIER_VIEW,
    ROLE_COURIER_LIST,
    ROLE_RE_GAME_HIGHLIGHT_LIST,
    ROLE_RE_RETAILER_GAME_VIEW_PAGE,
} = permissions;

const RoutesWrapper = () => (
    <Routes>
        {!COGNITO_ENABLED && (
            <Route path={LOGIN_PAGE} element={<LoginPage />} />
        )}

        <Route
            path={DASHBOARD}
            element={
                <ProtectedRoute
                    path={DASHBOARD}
                    component={<Dashboard />}
                    hasRole={DASHBOARD_PAGE}
                />
            }
        />
        <Route
            path={CONFIGURATION_MANAGEMENT}
            element={
                <ProtectedRoute
                    path={CONFIGURATION_MANAGEMENT}
                    component={<ConfigurationManagementPage />}
                    hasRole={VIEW_SYS_CONFIGS}
                />
            }
        />

        <Route
            path={PERMISSIONS_CONFIGURATION_MANAGEMENT}
            element={
                <ProtectedRoute
                    path={PERMISSIONS_CONFIGURATION_MANAGEMENT}
                    component={<PermissionsManagementPage />}
                    hasRole={VIEW_SYS_CONFIGS}
                />
            }
        />

        <Route
            path={CREATE_ROLE}
            element={
                <ProtectedRoute
                    path={CREATE_ROLE}
                    component={<CreateRolePage />}
                    hasRole={CREATE_ROLES}
                />
            }
        />

        <Route
            path={CONTACT_US_CONFIGURATION}
            element={
                <ProtectedRoute
                    path={CONTACT_US_CONFIGURATION}
                    component={<ContactUsConfigurationPage />}
                    hasRole={UPDATE_CONTACT_US}
                />
            }
        />

        <Route
            path={`${UPDATE_CONFIGURATION_MANAGEMENT}/:key`}
            element={
                <ProtectedRoute
                    path={`${UPDATE_CONFIGURATION_MANAGEMENT}/:key`}
                    component={<UpdateConfigurationManagementPage />}
                    hasAnyRole={[UPDATE_SYS_CONFIGS, LOGO_CONFIG_UPLOAD]}
                />
            }
        />

        <Route
            path={CONTACT_US_PAGE}
            element={
                <ProtectedRoute
                    path={CONTACT_US_PAGE}
                    component={<ContactUsPage />}
                    hasRole={CONTACT_US}
                />
            }
        />

        <Route
            path={CREATE_USER}
            element={
                <ProtectedRoute
                    path={CREATE_USER}
                    component={<CreateUser />}
                    hasRole={CREATE_USERS}
                />
            }
        />

        <Route
            path={`${UPDATE_USER}/:username`}
            element={
                <ProtectedRoute
                    path={`${UPDATE_USER}/:username`}
                    component={<UpdateUser />}
                    hasAnyRole={[CREATE_USERS, VIEW_USERS]}
                />
            }
        />

        <Route
            path={USER_MANAGEMENT}
            element={
                <ProtectedRoute
                    path={USER_MANAGEMENT}
                    component={<UsersPage />}
                    hasRole={LIST_USERS}
                />
            }
        />
        <Route
            path={GAMES}
            element={
                <ProtectedRoute
                    path={GAMES}
                    component={<GameCatalogPage />}
                    hasRole={GAME_PAGE}
                />
            }
        />

        <Route
            path={RETAILER_PLANOGRAM_LIST}
            element={
                <ProtectedRoute
                    path={RETAILER_PLANOGRAM_LIST}
                    component={<RetailerPlanogramListPage />}
                    hasRole={ROLE_PLANOGRAM_LIST}
                />
            }
        />

        <Route
            path={`${ORDER}/:id`}
            element={
                <ProtectedRoute
                    path={`${ORDER}/:id`}
                    component={<OrderDetailPage />}
                    hasRole={ORDER_DETAILS}
                />
            }
        />

        <Route
            path={orderTabs.IN_PROGRESS}
            element={
                <ProtectedRoute
                    path={orderTabs.IN_PROGRESS}
                    component={<OrderHistoryPage />}
                    hasRole={IN_PROGRESS_ORDERS}
                />
            }
        />

        <Route
            path={orderTabs.PAST}
            element={
                <ProtectedRoute
                    path={orderTabs.PAST}
                    component={<OrderHistoryPage />}
                    hasRole={PAST_ORDERS}
                />
            }
        />

        <Route
            path={`${INVOICE}/:id`}
            element={
                <ProtectedRoute
                    path={`${INVOICE}/:id`}
                    component={<InvoiceDetailPage />}
                    hasRole={INVOICE_DETAILS}
                />
            }
        />

        <Route
            path={INVOICE}
            element={
                <ProtectedRoute
                    path={INVOICE}
                    component={<InvoicesPage />}
                    hasRole={INVOICE_PAGE}
                />
            }
        />

        <Route
            path={`${REPORTS}/:name/:id`}
            element={
                <ProtectedRoute
                    path={`${REPORTS}/:name/:id`}
                    component={<ReportPage />}
                    hasRole="*"
                />
            }
        />

        <Route
            path={REPORTS}
            element={
                <ProtectedRoute
                    path={REPORTS}
                    component={<ReportsPage />}
                    hasRole={LIST_REPORT}
                />
            }
        />

        <Route
            path={FAQ_MANAGEMENT_PRIVATE}
            element={
                <ProtectedRoute
                    path={FAQ_MANAGEMENT_PRIVATE}
                    component={<AdminFaqPage />}
                    hasRole={CREATE_FAQ}
                />
            }
        />

        <Route
            path={FAQ_MANAGEMENT_PUBLIC}
            element={
                <ProtectedRoute
                    path={FAQ_MANAGEMENT_PUBLIC}
                    component={<AdminFaqPage />}
                    hasRole={CREATE_FAQ}
                />
            }
        />

        <Route
            path={FAQ_PAGE}
            element={
                <ProtectedRoute
                    path={FAQ_PAGE}
                    component={<FaqPage />}
                    hasRole={FAQ}
                />
            }
        />

        <Route
            path={PRIVACY_POLICY_PAGE}
            element={
                <ProtectedRoute
                    path={PRIVACY_POLICY_PAGE}
                    component={<PrivacyPolicyPage />}
                    hasRole={PRIVACY_POLICY}
                />
            }
        />

        <Route
            path={PRIVACY_POLICY_MANAGEMENT}
            element={
                <ProtectedRoute
                    path={PRIVACY_POLICY_MANAGEMENT}
                    component={<AdminPrivacyPolicyPage />}
                    hasRole={CREATE_PRIVACY_POLICY}
                />
            }
        />
        <Route
            path={NEW_RETAILER_POLICY}
            element={
                <ProtectedRoute
                    path={NEW_RETAILER_POLICY}
                    component={<AdminNewRetailerPage />}
                    hasRole={CREATE_NEW_RETAILER_POLICY}
                />
            }
        />

        <Route
            path={UPLOAD_MANAGEMENT}
            element={
                <ProtectedRoute
                    path={UPLOAD_MANAGEMENT}
                    component={<DocumentListPage />}
                    hasRole={CREATE_PRIVACY_POLICY}
                />
            }
        />

        <Route
            path={GAMES_LIST}
            element={
                <ProtectedRoute
                    path={GAMES_LIST}
                    component={<GamesListPage />}
                    hasRole={DISPLAY_GAMES_LIST}
                />
            }
        />
        <Route
            path={GAMES_REVIEW}
            element={
                <ProtectedRoute
                    path={GAMES_REVIEW}
                    component={<GameCatalogPage isImpersonateRetailer={true} />}
                    hasRole={ROLE_RE_RETAILER_GAME_VIEW_PAGE}
                />
            }
        />
        {
            // TO BE COMPLETED IN A FUTURE STORY
            // <ProtectedRoute
            //     path={VIRTUAL_TOUR_CONFIGURATION}
            //     component={VirtualTourConfigPage}
            //     hasRole={UPDATE_SYS_CONFIGS}
            // />
        }
        <Route
            path={MESSAGE_MANAGEMENT}
            element={
                <ProtectedRoute
                    path={MESSAGE_MANAGEMENT}
                    component={<MessageListPage />}
                    hasRole={UPDATE_SYS_CONFIGS}
                />
            }
        />
        <Route
            path={GAME_HIGHLIGHTS}
            element={
                <ProtectedRoute
                    path={GAME_HIGHLIGHTS}
                    component={<GameHighlightsPage />}
                    hasRole={ROLE_RE_GAME_HIGHLIGHT_LIST}
                />
            }
        />
        <Route
            path={CHECKOUT}
            element={
                <ProtectedRoute
                    path={CHECKOUT}
                    component={<CheckoutPage />}
                    hasRole={GAME_PAGE}
                />
            }
        />
        <Route
            path={DASHBOARD_SETTINGS}
            element={
                <ProtectedRoute
                    path={DASHBOARD_SETTINGS}
                    component={<DashboardSettings />}
                    hasRole={DASHBOARD_PAGE}
                />
            }
        />
        <Route
            path={RETAILER_GROUP_CREATE}
            element={
                <ProtectedRoute
                    path={RETAILER_GROUP_CREATE}
                    component={<EditRetailerGroupPage />}
                    hasRole={[CREATE_RETAILER_GROUP_LIST]}
                />
            }
        />
        <Route
            path={`${RETAILER_GROUP_UPDATE}/:id`}
            element={
                <ProtectedRoute
                    path={`${RETAILER_GROUP_UPDATE}/:id`}
                    component={<EditRetailerGroupPage />}
                    hasRole={[
                        UPDATE_RETAILER_GROUP_LIST,
                        VIEW_RETAILER_GROUP_LIST,
                    ]}
                />
            }
        />
        <Route
            path={RETAILER_GROUP_LIST}
            element={
                <ProtectedRoute
                    path={RETAILER_GROUP_LIST}
                    component={<RetailerGroupListPage />}
                    hasRole={DISPLAY_RETAILER_GROUP_LIST}
                />
            }
        />

        <Route
            path={COURIER_CREATE}
            element={
                <ProtectedRoute
                    path={COURIER_CREATE}
                    component={<EditCourierPage />}
                    hasRole={[ROLE_COURIER_CREATE]}
                />
            }
        />
        <Route
            path={`${COURIER_UPDATE}/:id`}
            element={
                <ProtectedRoute
                    path={`${COURIER_UPDATE}/:id`}
                    component={<EditCourierPage />}
                    hasRole={[ROLE_COURIER_UPDATE, ROLE_COURIER_VIEW]}
                />
            }
        />
        <Route
            path={COURIER_LIST}
            element={
                <ProtectedRoute
                    path={COURIER_LIST}
                    component={<CourierListPage />}
                    hasRole={ROLE_COURIER_LIST}
                />
            }
        />

        <Route
            path={PLANOGRAM_CREATE}
            element={
                <ProtectedRoute
                    path={PLANOGRAM_CREATE}
                    component={<EditPlanogramPage />}
                    hasRole={[ROLE_PLANOGRAM_CREATE]}
                />
            }
        />
        <Route
            path={`${PLANOGRAM_UPDATE}/:id`}
            element={
                <ProtectedRoute
                    path={`${PLANOGRAM_UPDATE}/:id`}
                    component={<EditPlanogramPage />}
                    hasRole={[ROLE_PLANOGRAM_UPDATE, ROLE_PLANOGRAM_VIEW]}
                />
            }
        />
        <Route
            path={PLANOGRAM_LIST}
            element={
                <ProtectedRoute
                    path={PLANOGRAM_LIST}
                    component={<PlanogramListPage />}
                    hasRole={ROLE_PLANOGRAM_LIST}
                />
            }
        />
        <Route
            path={`${RETAILER_RETURN_CREATE}/:id`}
            element={
                <ProtectedRoute
                    path={`${RETAILER_RETURN_CREATE}/:id`}
                    component={<ViewReturn />}
                    hasRole={ROLE_RETURN_VIEW}
                />
            }
        />
        <Route
            path={RETAILER_RETURN_CREATE}
            element={
                <ProtectedRoute
                    path={RETAILER_RETURN_CREATE}
                    component={<CreateReturn />}
                    hasRole={ROLE_RETURN_CREATE}
                />
            }
        />
        <Route
            path={RETAILER_RETURN_LIST}
            element={
                <ProtectedRoute
                    path={RETAILER_RETURN_LIST}
                    component={<ReturnsListPage />}
                    hasRole={ROLE_RETURN_LIST}
                />
            }
        />

        <Route path={GETTING_STARTED} element={<GettingStartedPage />} />
        <Route path={UNAUTHORIZED} element={<Unauthorized />} />
        <Route path={NOT_FOUND} element={<NotFound />} />
        {COGNITO_ENABLED && (
            <Route exact path="/" element={<CognitoLoadingScreen />} />
        )}
        {!COGNITO_ENABLED && (
            <Route exact path="/" element={<Navigate to={hasPermission()} />} />
        )}

        <Route element={<Navigate to={NOT_FOUND} />} />
    </Routes>
);

export default RoutesWrapper;
